import React, { useCallback, useMemo } from "react";
import { attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { soundManager } from "../../../../SoundManager";
import { incrementBet, closeMenu } from "../../../../State/actions";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import {
  selectIsSpinning,
  selectUserInterfaceDisabled,
  selectSessionExpired,
  selectBet,
  selectBets,
  selectBalance,
  selectAutoPlayActive,
  selectHoldAndWinActive,
  selectFreeRoundsActive,
} from "../../../../State/selectors";
import { PixiSettings } from "../../../../pixiSettings";

const BET_CONTROL_BUTTON_CONFIG = {
  BASE_WIDTH: 45,
  BASE_HEIGHT: 45,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.4,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.2,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.2,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.2,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

const ButtonIncrementBet = ({ x = 0, y = 0 }) => {
  const dispatch = useDispatch();
  const { getResponsiveConfig, device } = useDimensions();

  const isSpinning = useSelector(selectIsSpinning);
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const balance = useSelector(selectBalance);
  const sessionExpired = useSelector(selectSessionExpired);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);

  // Scale hover + press effect
  const { hoverContainerRef, buttonRef, handlePointerOver, handlePointerOut, triggerPress } = useButtonHover();

  const texture = getUITexture("icon_plus");

  // Memoize calculations
  const currentBetIndex = useMemo(() => bets.indexOf(bet), [bets, bet]);
  const nextBetAmount = useMemo(
    () => bets[currentBetIndex + 1],
    [bets, currentBetIndex]
  );

  const canIncreaseBet = useMemo(
    () =>
      currentBetIndex < bets.length - 1 &&
      balance >= nextBetAmount &&
      !isSpinning &&
      !sessionExpired &&
      !autoPlayActive &&
      !userInterfaceDisabled &&
      !holdAndWinActive &&
      !(FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive),
    [
      currentBetIndex,
      bets.length,
      balance,
      nextBetAmount,
      isSpinning,
      sessionExpired,
      autoPlayActive,
      userInterfaceDisabled,
      holdAndWinActive,
      freeRoundsActive,
    ]
  );

  // Get responsive configuration from context - memoized
  const buttonConfig = useMemo(
    () => getResponsiveConfig(BET_CONTROL_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  const handleClick = useCallback(() => {
    dispatch(closeMenu());

    attemptFullscreenToggle(dispatch, device);

    if (canIncreaseBet) {
      soundManager.play(SOUND_NAME.button_click);
      triggerPress();
      dispatch(incrementBet());
    }
  }, [canIncreaseBet, dispatch, device, triggerPress]);

  return (
    <Container x={x} y={y}>
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="secondary_button_increment_bet"
          anchor={{ x: 0, y: 0.5 }}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode={canIncreaseBet ? "static" : "none"}
          cursor={canIncreaseBet ? "pointer" : "default"}
          alpha={canIncreaseBet ? 1 : 0.5}
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
        />
      </Container>
    </Container>
  );
};

export default ButtonIncrementBet;
